import React from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { Formik, Form } from "formik";
import * as yup from "yup";
import OrderFormFieldViewSimple from "./forms/OrderFormFieldViewSimple";

const OrderFormFieldValueEdit = ({
  canEdit,
  orderFormField,
  eventOrderFormField,
  index,
  editing,
  setEditing,
  ticket,
  handleUpdate,
}) => {
  const { name, nameForTicket, value, type, key } = orderFormField;
  const { internalOnly } = eventOrderFormField || { internalOnly: false };
  let orderFormFieldName = internalOnly ? `${name} (internal use only)` : name;
  let nameText = ticket && nameForTicket ? nameForTicket : orderFormFieldName;
  let valueText;
  let initialValues;
  let validationSchema;
  switch (type) {
    case "smallText":
    case "largeText":
      valueText = value || "<unanswered>";
      initialValues = { editingOrderForm: value || "" };
      validationSchema = yup.object({
        editingOrderForm: yup.string(),
      });
      break;
    case "checkbox":
      valueText = value ? "true" : "false";
      initialValues = { editingOrderForm: value };
      validationSchema = yup.object({
        editingOrderForm: yup.boolean(),
      });
      break;
    case "multiSelectDropdown":
    case "multiSelectRadio":
      valueText = value?.name || "<unanswered>";
      initialValues = { editingOrderForm: value?.key };
      validationSchema = yup.object({
        editingOrderForm: yup.string(),
      });
      break;
    case "multiSelectCheckbox":
      if (value && Object.keys(value).length > 0) {
        valueText = Object.keys(value)
          .map((i) => value[i].name)
          .join(", ");
        initialValues = {
          editingOrderForm: Object.keys(value).map(
            (valueKey) => value[valueKey].key,
          ),
        };
      } else {
        valueText = "<none>";
        initialValues = {
          editingOrderForm: [],
        };
      }
      validationSchema = yup.object({
        editingOrderForm: yup.array(),
      });
      break;
    default:
      valueText = value;
      initialValues = { editingOrderForm: value || "" };
      validationSchema = yup.object({
        editingOrderForm: yup.string(),
      });
  }

  const allowEdit =
    canEdit &&
    (eventOrderFormField != null ||
      key === "firstName" ||
      key === "lastName" ||
      key === "email");
  return (
    <Box>
      <Box alignItems={"center"} width={"100%"} gap={"5px"}>
        {editing ? (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, submitProps) => {
              let valuePayload;
              switch (type) {
                case "smallText":
                case "largeText":
                case "checkbox":
                  valuePayload = values.editingOrderForm;
                  break;
                case "multiSelectDropdown":
                case "multiSelectRadio":
                  const selectedOption = eventOrderFormField.options.find(
                    ({ key }) => key === values.editingOrderForm,
                  );
                  const { key, name } = selectedOption;
                  valuePayload = {
                    key,
                    name,
                  };
                  break;
                case "multiSelectCheckbox":
                  valuePayload = values.editingOrderForm.reduce(
                    (acc, selectedOptionKey, index) => {
                      const selectedOption = eventOrderFormField.options.find(
                        ({ key }) => key === selectedOptionKey,
                      );
                      const { key, name } = selectedOption;
                      return { ...acc, [index]: { name, key } };
                    },
                    {},
                  );
                  break;
                default:
                  valuePayload = values.editingOrderForm;
              }
              await handleUpdate(valuePayload);
              setEditing(false);
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              isValid,
              dirty,
              setValues,
              setFieldValue,
              handleReset,
              submitForm,
            }) => {
              return (
                <Form noValidate autoComplete="off">
                  <>
                    <Box display={"flex"} alignItems={"center"} gap={"10px"}>
                      <Typography
                        flexGrow={"1"}
                        sx={{ fontWeight: "light", marginTop: "10px" }}
                      >
                        {nameText}
                      </Typography>
                      <Box>
                        <Tooltip title="Save">
                          <IconButton
                            type="submit"
                            sx={{
                              height: "40px",
                              width: "40px",
                              marginTop: "10px",
                            }}
                            onClick={(e) => {}}
                          >
                            <CheckIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            sx={{
                              height: "40px",
                              width: "40px",
                              marginTop: "10px",
                            }}
                            onClick={(e) => {
                              setEditing(false);
                            }}
                          >
                            <CancelIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <OrderFormFieldViewSimple
                      {...{
                        formikProps: {
                          values,
                          touched,
                          errors,
                          handleChange,
                          handleBlur,
                          setFieldValue,
                        },
                        orderFormField: eventOrderFormField,
                        ticket,
                        index,
                      }}
                    />
                  </>
                </Form>
              );
            }}
          </Formik>
        ) : (
          <Box paddingTop={"10px"} paddingBottom={"10px"}>
            <Box display={"flex"} alignItems={"center"} gap={"10px"}>
              <Typography flexGrow={"1"} sx={{ fontWeight: "light" }}>
                {orderFormFieldName}
              </Typography>
              <Tooltip title={allowEdit ? "Edit" : "Can't edit"}>
                <div>
                  <IconButton
                    disabled={!allowEdit}
                    onClick={(e) => {
                      setEditing(true);
                    }}
                  >
                    <EditIcon color={allowEdit ? "primary" : "disabled"} />
                  </IconButton>
                </div>
              </Tooltip>
            </Box>

            <Typography flexGrow={"1"}>{valueText}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrderFormFieldValueEdit;
